import type { Flights } from "@b2bportal/air-shopping-api";
import type { ITripTerminus } from "@hopper-b2b/types";
import { API_DATE_FORMAT } from "@hopper-b2b/utilities";
import dayjs from "dayjs";
import { isEqual } from "lodash-es";

import { normalizePassengerCounts } from "../../search/utils/passengerCounts";

/**
 * The parameters a shop request was made with, normalized so that values
 * derived from flightSearch state (Date objects, possibly-null counts) and
 * values used by the fetch sagas (YYYY-MM-DD strings, defaulted counts)
 * always compare equal for the same search. Stored in flightShop alongside
 * the flights response so stale responses can be detected (ATL-1497).
 */
export interface IFlightsRequestParams {
  originCode: string;
  originRegionType: string;
  destinationCode: string;
  destinationRegionType: string;
  departureDate: string;
  returnDate: string | null;
  adultsCount: number;
  childrenCount: number;
  infantsInSeatCount: number;
  infantsOnLapCount: number;
}

export interface IBuildFlightsRequestParamsInput {
  origin: ITripTerminus | null | undefined;
  destination: ITripTerminus | null | undefined;
  departureDate: Date | string | null | undefined;
  returnDate?: Date | string | null;
  adultsCount?: number | null;
  childrenCount?: number | null;
  infantsInSeatCount?: number | null;
  infantsOnLapCount?: number | null;
}

// Both the sagas (when recording params) and the selector (when deriving the
// current search's params) MUST build through this function; hand-rolled
// params objects risk normalization drift and refetch loops.
export const buildFlightsRequestParams = ({
  origin,
  destination,
  departureDate,
  returnDate,
  adultsCount,
  childrenCount,
  infantsInSeatCount,
  infantsOnLapCount,
}: IBuildFlightsRequestParamsInput): IFlightsRequestParams | null => {
  const originCode = origin?.id?.code;
  const destinationCode = destination?.id?.code;
  if (!originCode?.code || !destinationCode?.code || !departureDate) {
    return null;
  }

  return {
    originCode: originCode.code,
    originRegionType: originCode.regionType,
    destinationCode: destinationCode.code,
    destinationRegionType: destinationCode.regionType,
    departureDate: dayjs(departureDate).format(API_DATE_FORMAT),
    returnDate: returnDate ? dayjs(returnDate).format(API_DATE_FORMAT) : null,
    ...normalizePassengerCounts({
      // The fetch sagas default a falsy adults count with `|| 1` when building
      // the request body; mirror that here (0 → null → minimum) so a 0 in
      // flightSearch state can never mismatch stored params and refetch-loop.
      adultsCount: adultsCount || null,
      childrenCount,
      infantsInSeatCount,
      infantsOnLapCount,
    }),
  };
};

export const flightsRequestParamsMatch = (
  stored: IFlightsRequestParams | null | undefined,
  current: IFlightsRequestParams | null | undefined
): boolean => {
  if (!stored || !current) {
    return false;
  }
  return isEqual(stored, current);
};

export const shouldFetchFlights = ({
  flights,
  flightsMatchSearch,
}: {
  flights: Flights | null;
  flightsMatchSearch: boolean;
}): boolean => !flights || !flightsMatchSearch;
